import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay';
import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import { HealthStatusCheck, HealthStatusChecksService } from '@lib/members/data-access';
import { TranslatePipe } from '@ngx-translate/core';
import { take } from 'rxjs';

import { MatCard, MatCardContent } from '@angular/material/card';
import { MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatInput } from '@angular/material/input';
import { MatTimepickerModule } from '@angular/material/timepicker';

import { DatepickerComponent } from '@lib/shared-ui/commons/datepicker/datepicker.component';

import {
  allowedTimeRangeValidator,
  combineDateAndTime,
  findNextScheduledCheck,
  futureOrTodayValidator,
  getDefaultScheduleDate,
  getDefaultScheduleTime,
  getFormScheduleErrors,
  getStartOfToday,
  isPlanned,
  isSent,
  noConflictOnSameDayValidator,
  todayMinOneHourValidator,
} from '../../util/health-status-check-rules';

interface ScheduleFormControls {
  date: FormControl<Date | null>;
  time: FormControl<Date | null>;
}

@Component({
  selector: 'members-health-status-check-scheduler',
  standalone: true,
  imports: [
    CdkOverlayOrigin,
    CdkConnectedOverlay,
    DatePipe,
    MatCard,
    MatCardContent,
    DatepickerComponent,
    MatFormField,
    MatIcon,
    MatInput,
    MatSuffix,
    MatTimepickerModule,
    ReactiveFormsModule,
    TranslatePipe,
  ],
  templateUrl: './health-status-check-scheduler.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HealthStatusCheckSchedulerComponent implements OnInit {
  private readonly service = inject(HealthStatusChecksService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly fb = inject(FormBuilder);

  readonly userId = input.required<string>();

  protected readonly items = signal<HealthStatusCheck[]>([]);
  protected readonly loading = signal<boolean>(false);
  protected readonly cardOpen = signal<boolean>(false);
  protected readonly addFormVisible = signal<boolean>(false);
  protected readonly errorMessage = signal<string | null>(null);
  protected readonly minScheduleDate = signal<Date>(getStartOfToday());

  protected readonly nextScheduleDate = computed<Date | null>(() => findNextScheduledCheck(this.items()));

  protected readonly sentItems = computed<HealthStatusCheck[]>(() =>
    this.items()
      .filter(item => isSent(item))
      .sort((a, b) => new Date(b.scheduledFor).getTime() - new Date(a.scheduledFor).getTime())
  );

  protected readonly plannedItems = computed<HealthStatusCheck[]>(() =>
    this.items()
      .filter(item => isPlanned(item))
      .sort((a, b) => new Date(a.scheduledFor).getTime() - new Date(b.scheduledFor).getTime())
  );

  protected readonly lastSent = computed<HealthStatusCheck | null>(() => this.sentItems()[0] ?? null);

  protected scheduleForm: FormGroup<ScheduleFormControls> = this.fb.group<ScheduleFormControls>(
    {
      date: this.fb.control<Date | null>(null, [Validators.required, futureOrTodayValidator()]),
      time: this.fb.control<Date | null>(null, [Validators.required, allowedTimeRangeValidator()]),
    },
    { validators: [todayMinOneHourValidator(), noConflictOnSameDayValidator(this.items)] }
  );

  protected readonly formError = signal<string | null>(null);

  protected readonly formErrorKey = computed<string | null>(() => {
    if (!this.formError()) return null;
    else return `healthStatusCheck.message.validation${this.formError().charAt(0).toUpperCase()}${this.formError().slice(1)}`;
  });

  protected readonly errorMessageKey = computed<string | null>(() => (this.errorMessage() ? `healthStatusCheck.message.${this.errorMessage()}` : null));

  ngOnInit(): void {
    this.fetchItems();
    this.watchScheduleForm();
  }

  protected toggleCard(): void {
    if (this.cardOpen()) this.closeCard();
    else {
      this.cardOpen.set(true);
      this.fetchItems();
    }
  }

  protected closeCard(): void {
    this.cardOpen.set(false);
    this.hideAddForm();
    this.errorMessage.set(null);
  }

  protected toggleAddForm(): void {
    if (this.addFormVisible()) this.hideAddForm();
    else {
      this.resetFormToDefaults();
      this.addFormVisible.set(true);
    }
  }

  protected hideAddForm(): void {
    this.addFormVisible.set(false);
    this.scheduleForm.reset();
    this.formError.set(null);
  }

  protected submit(): void {
    if (this.scheduleForm.invalid || this.loading()) return;
    else this.errorMessage.set(null);
    this.loading.set(true);

    const date = this.scheduleForm.controls.date.value!;
    const time = this.scheduleForm.controls.time.value!;
    const scheduledFor = combineDateAndTime(date, time).toISOString();

    this.service
      .create(this.userId(), { scheduledFor })
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: created => {
          this.items.update(items => [created, ...items]);
          this.hideAddForm();
          this.loading.set(false);
        },
        error: error => {
          console.error('Failed to create health status check', error);
          this.errorMessage.set('createError');
          this.loading.set(false);
        },
      });
  }

  protected cancel(item: HealthStatusCheck): void {
    if (this.loading()) return;

    this.errorMessage.set(null);
    this.loading.set(true);
    this.service
      .cancel(this.userId(), item.id)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.items.update(items => items.filter(i => i.id !== item.id));
          this.loading.set(false);
        },
        error: error => {
          console.error('Failed to cancel health status check', error);
          this.errorMessage.set('cancelError');
          this.loading.set(false);
        },
      });
  }

  private watchScheduleForm() {
    this.scheduleForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.formError.set(getFormScheduleErrors(this.scheduleForm));
    });
  }

  private fetchItems(): void {
    this.errorMessage.set(null);
    this.loading.set(true);
    this.service
      .list(this.userId())
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: items => {
          this.items.set(items ?? []);
          this.loading.set(false);
        },
        error: error => {
          console.error('Failed to load health status checks', error);
          this.errorMessage.set('loadError');
          this.loading.set(false);
        },
      });
  }

  private resetFormToDefaults(): void {
    this.minScheduleDate.set(getStartOfToday());
    this.scheduleForm.reset({
      date: getDefaultScheduleDate(),
      time: getDefaultScheduleTime(),
    });
    this.formError.set(getFormScheduleErrors(this.scheduleForm));
  }
}
